import { useState } from 'react';
import { Modal } from '../../Components/Modal/Modal';
import { useLoader } from '../../contexts/LoaderContext';
import { useAlert } from '../../contexts/AlertContext';
import useEventList from '../../hooks/useEventsList';
import { events } from '../../api/events';
import { EventsContent } from './components/EventsContent';
import { EventsHeader } from './components/EventsHeader';
import { AddEventForm } from '../../Components/EventsForm/AddEventForm';
import { AddEventFormValues } from '../../Components/EventsForm/types';
import { Event } from '../../types/event';
import { useFormatImageUrl } from '../../utils/formatImageUrl';
import { parseServerDate } from '../../utils/dateFormatters';
import './Events.scss';

export const Events = () => {
  const { showLoader, hideLoader } = useLoader();
  const { showAlert } = useAlert();
  const formatImageUrl = useFormatImageUrl();
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const { currentPage, onPageChange, onDeleteEvent, eventsData, fetchEvents } = useEventList({
    itemsPerPage: 8,
    startDate,
    endDate,
  });

  const openEventModal = () => {
    setIsEditMode(false);
    setSelectedEvent(null);
    setIsEventModalOpen(true);
  };

  const openEditModal = (eventItem: Event) => {
    setIsEditMode(true);
    setSelectedEvent(eventItem);
    setIsEventModalOpen(true);
  };

  const closeEventModal = () => {
    setIsEventModalOpen(false);
    setIsEditMode(false);
    setSelectedEvent(null);
  };

  const handleEventSubmit = async (values: AddEventFormValues) => {
    try {
      showLoader();

      const processRichTextField = (value: string | undefined) => {
        if (value === '<p><br></p>') return '';
        return value || '';
      };

      const processedValues = {
        name: values.name,
        description: values.description,
        startDate: values.startDate!,
        endDate: values.endDate!,
        option1: processRichTextField(values.option1 || ''),
        option2: processRichTextField(values.option2 || ''),
        option3: processRichTextField(values.option3 || ''),
      };

      if (isEditMode && selectedEvent) {
        await events.update(selectedEvent.id, {
          ...processedValues,
          ...(values.image && { image: values.image }),
        });
        showAlert('Event updated successfully', 'success');
      } else {
        if (!values.image) {
          showAlert('Image is required', 'error');
          return;
        }

        await events.create({
          ...processedValues,
          image: values.image,
        });
        showAlert('Event created successfully', 'success');
      }
      await fetchEvents();
      closeEventModal();
    } catch (error) {
      showAlert('Failed to save event', 'error');
    } finally {
      hideLoader();
    }
  };

  return (
    <div className="events-page">
      <EventsHeader
        onAddEvent={openEventModal}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
      />
      <EventsContent
        eventsData={eventsData}
        currentPage={currentPage}
        onPageChange={onPageChange}
        onDeleteEvent={onDeleteEvent}
        onEditEvent={openEditModal}
      />
      <Modal isOpen={isEventModalOpen} onClose={closeEventModal}>
        <AddEventForm
          onSubmit={handleEventSubmit}
          onCancel={closeEventModal}
          isEditMode={isEditMode}
          existingImageUrl={selectedEvent?.image ? formatImageUrl(selectedEvent.image) : undefined}
          initialValues={
            selectedEvent
              ? {
                  name: selectedEvent.name,
                  description: selectedEvent.description,
                  startDate: parseServerDate(selectedEvent.startDate),
                  endDate: parseServerDate(selectedEvent.endDate),
                  option1: selectedEvent.option1,
                  option2: selectedEvent.option2,
                  image: null,
                }
              : undefined
          }
        />
      </Modal>
    </div>
  );
};
